export type ReturnEvidenceRequirement = 'HIDDEN' | 'OPTIONAL' | 'REQUIRED';
export type ReturnEvidenceDefault = 'REASON_BASED' | ReturnEvidenceRequirement;
export type ReturnEvidenceField = 'notes' | 'photos';
export type ReturnEvidenceOverrideValue = 'INHERIT' | ReturnEvidenceRequirement;

export interface ReturnEvidenceRequirements {
  defaults: Record<ReturnEvidenceField, ReturnEvidenceDefault>;
  byReason: Record<
    string,
    Partial<Record<ReturnEvidenceField, ReturnEvidenceRequirement>>
  >;
}

const DEFAULT_REQUIREMENTS: ReturnEvidenceRequirements = {
  defaults: {
    notes: 'REASON_BASED',
    photos: 'REASON_BASED',
  },
  byReason: {},
};

const isRecord = (value: unknown): value is Record<string, any> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isRequirement = (value: unknown): value is ReturnEvidenceRequirement =>
  value === 'HIDDEN' || value === 'OPTIONAL' || value === 'REQUIRED';

const isDefault = (value: unknown): value is ReturnEvidenceDefault =>
  value === 'REASON_BASED' || isRequirement(value);

export const normalizeReturnEvidenceRequirements = (
  customerExperience: unknown,
): ReturnEvidenceRequirements => {
  const experience = isRecord(customerExperience) ? customerExperience : {};
  const evidenceRequirements = isRecord(experience.evidenceRequirements)
    ? experience.evidenceRequirements
    : {};
  const storedDefaults = isRecord(evidenceRequirements.defaults)
    ? evidenceRequirements.defaults
    : {};
  const storedByReason = isRecord(evidenceRequirements.byReason)
    ? evidenceRequirements.byReason
    : {};
  const byReason: ReturnEvidenceRequirements['byReason'] = {};

  Object.entries(storedByReason).forEach(([reasonId, storedRule]) => {
    if (!isRecord(storedRule)) return;

    const rule: Partial<
      Record<ReturnEvidenceField, ReturnEvidenceRequirement>
    > = {};

    if (isRequirement(storedRule.notes)) rule.notes = storedRule.notes;
    if (isRequirement(storedRule.photos)) rule.photos = storedRule.photos;
    if (Object.keys(rule).length > 0) byReason[reasonId] = rule;
  });

  return {
    defaults: {
      notes: isDefault(storedDefaults.notes)
        ? storedDefaults.notes
        : experience.requireCustomerNotes === true
        ? 'REQUIRED'
        : DEFAULT_REQUIREMENTS.defaults.notes,
      photos: isDefault(storedDefaults.photos)
        ? storedDefaults.photos
        : experience.requireReturnPhotos === true
        ? 'REQUIRED'
        : DEFAULT_REQUIREMENTS.defaults.photos,
    },
    byReason,
  };
};

export const updateReasonEvidenceOverride = (
  current: ReturnEvidenceRequirements['byReason'],
  reasonId: string,
  field: ReturnEvidenceField,
  value: ReturnEvidenceOverrideValue,
): ReturnEvidenceRequirements['byReason'] => {
  const next = { ...current };
  const nextRule = { ...next[reasonId] };

  if (value === 'INHERIT') {
    delete nextRule[field];
  } else {
    nextRule[field] = value;
  }

  if (Object.keys(nextRule).length === 0) {
    delete next[reasonId];
  } else {
    next[reasonId] = nextRule;
  }

  return next;
};
